import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Download, Send, Loader2, Linkedin } from "lucide-react";
import { PROFILE } from "@/data/content";
import { Reveal, ChapterBadge } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Thank you — I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please email me directly instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-parchment/[0.04] border border-parchment/15 rounded-xl px-4 py-3.5 text-sm text-parchment placeholder:text-parchment/35 focus:outline-none focus:border-copper-500 focus:ring-1 focus:ring-copper-500 transition-[border-color] duration-200";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28 bg-charcoal-900 text-parchment relative overflow-hidden">
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -left-10 -bottom-16 font-serif italic text-[22vw] leading-none text-parchment/[0.03]"
      >
        Hello
      </span>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <ChapterBadge number="13" label="Contact" light />

        <div className="mt-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
              Have a creative challenge <em className="italic text-copper-500">to solve?</em>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-parchment/65 leading-relaxed max-w-md">
              Let's talk about creative operations, content, design, brand communication, or
              building scalable creative workflows.
            </p>

            <div className="mt-10 flex flex-col gap-5">
              <a
                data-testid="contact-email-link"
                href={`mailto:${PROFILE.email}`}
                className="group flex items-center gap-4 text-parchment/80 hover:text-parchment transition-colors"
              >
                <span className="w-11 h-11 rounded-full border border-parchment/15 flex items-center justify-center text-copper-500 group-hover:border-copper-500/60 transition-colors">
                  <Mail className="w-4.5 h-4.5" />
                </span>
                <span className="text-sm sm:text-base">{PROFILE.email}</span>
              </a>
              <a
                data-testid="contact-phone-link"
                href={`tel:${PROFILE.phone.replace(/\s/g, "")}`}
                className="group flex items-center gap-4 text-parchment/80 hover:text-parchment transition-colors"
              >
                <span className="w-11 h-11 rounded-full border border-parchment/15 flex items-center justify-center text-copper-500 group-hover:border-copper-500/60 transition-colors">
                  <Phone className="w-4.5 h-4.5" />
                </span>
                <span className="text-sm sm:text-base">{PROFILE.phone}</span>
              </a>
              <div className="flex items-center gap-4 text-parchment/80" data-testid="contact-location">
                <span className="w-11 h-11 rounded-full border border-parchment/15 flex items-center justify-center text-copper-500">
                  <MapPin className="w-4.5 h-4.5" />
                </span>
                <span className="text-sm sm:text-base">{PROFILE.location}</span>
              </div>
              {PROFILE.linkedin && (
                <a
                  data-testid="contact-linkedin-link"
                  href={PROFILE.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 text-parchment/80 hover:text-parchment transition-colors"
                >
                  <span className="w-11 h-11 rounded-full border border-parchment/15 flex items-center justify-center text-copper-500 group-hover:border-copper-500/60 transition-colors">
                    <Linkedin className="w-4.5 h-4.5" />
                  </span>
                  <span className="text-sm sm:text-base">LinkedIn Profile</span>
                </a>
              )}
            </div>

            <a
              data-testid="resume-download-button"
              href={PROFILE.resumeUrl}
              download="Sahil-Paruthi-Resume.pdf"
              className="mt-10 inline-flex items-center gap-2.5 rounded-full border border-parchment/25 text-parchment text-sm font-medium px-7 py-3.5 hover:border-copper-500 hover:text-copper-500 transition-colors duration-300"
            >
              <Download className="w-4 h-4" />
              Download Resume
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-2xl border border-parchment/10 bg-parchment/[0.03] p-7 sm:p-9 flex flex-col gap-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  aria-label="Your name"
                  className={inputCls}
                />
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Your email"
                  aria-label="Your email"
                  className={inputCls}
                />
              </div>
              <input
                data-testid="contact-subject-input"
                value={form.subject}
                onChange={update("subject")}
                placeholder="Subject (optional)"
                aria-label="Subject"
                className={inputCls}
              />
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell me about your creative challenge..."
                aria-label="Your message"
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-copper-600 text-parchment text-sm font-medium px-7 py-3.5 hover:bg-copper-500 disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
